import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../api/client';
import { Announcement } from '../../types/announcement';
import './EventAnnouncements.css';

interface EventAnnouncementsProps {
  eventId: string;
}

const EventAnnouncements: React.FC<EventAnnouncementsProps> = ({ eventId }) => {
  const { t } = useTranslation();
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchAnnouncements = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await api.announcements.forEvent(eventId);
      setAnnouncements(data as Announcement[]);
    } catch (err) {
      console.error('Failed to fetch event announcements:', err);
    } finally {
      setLoading(false);
    }
  }, [eventId]);

  useEffect(() => {
    fetchAnnouncements();
  }, [fetchAnnouncements]);

  const formatTime = (timestamp: string): string => {
    const date = new Date(timestamp);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return t('comments.justNow');
    if (minutes < 60) return t('comments.minutesAgo', { count: minutes });
    if (hours < 24) return t('comments.hoursAgo', { count: hours });
    if (days < 7) return t('comments.daysAgo', { count: days });
    return date.toLocaleDateString();
  };

  if (loading) {
    return (
      <div className="event-announcements__loading">
        <div className="event-announcements__spinner"></div>
      </div>
    );
  }

  if (announcements.length === 0) {
    return null;
  }

  return (
    <div className="event-announcements">
      <h4 className="event-announcements__title">{t('announcements.title')}</h4>
      <div className="event-announcements__list">
        {announcements.map((announcement) => (
          <div key={announcement._id} className="event-announcements__item">
            <div className="event-announcements__avatar">
              {announcement.sender.name.charAt(0).toUpperCase()}
            </div>
            <div className="event-announcements__content">
              <div className="event-announcements__header">
                <span className="event-announcements__author">{announcement.sender.name}</span>
                <span className="event-announcements__time">{formatTime(announcement.createdAt)}</span>
              </div>
              <p className="event-announcements__text">{announcement.message}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default EventAnnouncements;
